import { useState, type CSSProperties } from 'react';
import { IconBack } from './components/icons';

// Evaluate/import P3 — the CONFIRM UI for the CRAFT-import. Consumes P2a's candidates (POST /api/parse), SHOWS
// the read, lets the user CORRECT it, and commits CONFIRMED entries. CALM reviewable list (Principle 5).
//
// FIREWALL — confirm-and-adopt: each candidate's `descriptive` pre-fills an EDITABLE bullet box. The system never
// auto-writes a bullet; the user edits/confirms and the COMMIT CLICK is the authorship act. A bullet is committed
// only if the user left text in its box AND the row is included. The commit itself goes through the EXISTING
// writer (POST /api/entries, via the parent's onCommit) — this component invents no write path.

export interface Candidate {
  section: string; title: string; organization: string; start_date: string; end_date: string; date_text: string;
  descriptive: string; source_block: string; block_verbatim: boolean;
}
export interface CommitEntry { section: string; title: string; organization: string; start_date: string; end_date: string; date_text: string; bullet: string }
interface Specialty { id: string; name: string }
interface Section { id: string; name: string; archetype: string }

interface Row extends CommitEntry { include: boolean; source_block: string; block_verbatim: boolean }

export function ImportConfirm({ candidates, specialties, sections, busy, error, onCommit, onCancel, lockedSpecialty }: {
  candidates: Candidate[];
  specialties: Specialty[];
  sections: Section[];
  busy: boolean;
  error: string;
  onCommit: (specialtyId: string, entries: CommitEntry[]) => void;
  onCancel: () => void;
  // EVALUATE flow: specialty was chosen up front (EvalSetup) → show it, don't re-ask. CRAFT leaves this undefined
  // and keeps the in-confirm picker.
  lockedSpecialty?: { id: string; name: string };
}) {
  const [pickedSpecialtyId, setPickedSpecialtyId] = useState('');
  const specialtyId = lockedSpecialty?.id ?? pickedSpecialtyId;
  const [rows, setRows] = useState<Row[]>(() => candidates.map((c) => ({
    include: true,
    section: c.section, title: c.title, organization: c.organization,
    start_date: c.start_date, end_date: c.end_date, date_text: c.date_text,
    bullet: c.descriptive, // confirm-and-adopt: the verbatim descriptive pre-fills the EDITABLE box
    source_block: c.source_block, block_verbatim: c.block_verbatim,
  })));

  function patch(i: number, p: Partial<Row>) { setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r))); }

  const included = rows.filter((r) => r.include);
  const needSection = included.filter((r) => !r.section).length;
  const canCommit = !!specialtyId && included.length > 0 && needSection === 0 && !busy;

  function commit() {
    if (!canCommit) return;
    onCommit(specialtyId, included.map((r) => ({
      section: r.section, title: r.title, organization: r.organization,
      start_date: r.start_date, end_date: r.end_date, date_text: r.date_text, bullet: r.bullet,
    })));
  }

  return (
    <main style={S.main} data-testid="import-confirm">
      <button className="btn btn-ghost" style={S.back} onClick={onCancel} data-testid="ic-cancel"><IconBack aria-hidden="true" /> Back</button>
      <h1>Here’s how we read your CV</h1>
      <p style={S.help} data-testid="ic-count">
        We found {rows.length} {rows.length === 1 ? 'entry' : 'entries'}. Check each one, fix anything we got wrong,
        and edit the bullet into your own words — then add them to your CV. Nothing is saved until you do.
      </p>

      {lockedSpecialty ? (
        <p style={S.locked} data-testid="ic-specialty-locked">Reading this CV for <strong>{lockedSpecialty.name}</strong>.</p>
      ) : (
        <label style={S.label}>Which specialty is this CV for?
          <select style={S.sel} value={specialtyId} onChange={(e) => setPickedSpecialtyId(e.target.value)} data-testid="ic-specialty">
            <option value="">— choose a specialty —</option>
            {specialties.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      )}

      {rows.map((r, i) => (
        <div key={i} style={{ ...S.card, ...(r.include ? {} : S.cardOff) }} data-testid={`ic-card-${i}`}>
          <div style={S.cardHead}>
            {/* section at the LEFT, include toggle at the RIGHT — aligned columns down the list (space-between). */}
            <div style={S.sectionWrap}>
              <span style={S.fieldName}>Section</span>
              <select style={S.sel} value={r.section} onChange={(e) => patch(i, { section: e.target.value })} data-testid={`ic-section-${i}`}>
                <option value="">— choose a section —</option>
                {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <label style={S.includeLbl}>
              <input type="checkbox" checked={r.include} onChange={(e) => patch(i, { include: e.target.checked })} data-testid={`ic-include-${i}`} />
              {r.include ? 'Include this entry' : 'Skipped'}
            </label>
          </div>

          {/* field NAMES are shaded chips (muted scaffolding) so they read distinct from the user's --ink values. */}
          <label style={S.label}><span style={S.fieldName}>Title / role</span><input style={S.input} value={r.title} onChange={(e) => patch(i, { title: e.target.value })} data-testid={`ic-title-${i}`} /></label>
          <label style={S.label}><span style={S.fieldName}>Organization</span><input style={S.input} value={r.organization} onChange={(e) => patch(i, { organization: e.target.value })} data-testid={`ic-org-${i}`} /></label>
          <div style={S.dates}>
            <label style={S.dateLbl}><span style={S.fieldName}>Start</span><input style={S.input} value={r.start_date} onChange={(e) => patch(i, { start_date: e.target.value, date_text: e.target.value ? '' : r.date_text })} data-testid={`ic-start-${i}`} /></label>
            <label style={S.dateLbl}><span style={S.fieldName}>End</span><input style={S.input} value={r.end_date} onChange={(e) => patch(i, { end_date: e.target.value, date_text: e.target.value ? '' : r.date_text })} data-testid={`ic-end-${i}`} /></label>
            <label style={S.dateLbl}><span style={S.fieldName}>or date (text)</span><input style={S.input} value={r.date_text} onChange={(e) => patch(i, { date_text: e.target.value })} data-testid={`ic-datetext-${i}`} /></label>
          </div>

          <label style={S.label}>
            <span style={S.fieldName}>Bullet</span><span style={S.fieldHint}> — <em>in your own words</em> (we pre-filled what you wrote; edit it, or clear it to skip)</span>
            <textarea style={S.ta} rows={2} value={r.bullet} onChange={(e) => patch(i, { bullet: e.target.value })} data-testid={`ic-bullet-${i}`} />
          </label>

          {!r.block_verbatim && <p style={S.warn} data-testid={`ic-warn-${i}`}>We couldn’t place this cleanly — please double-check the details above.</p>}
          {r.source_block && (
            <details style={S.details}>
              <summary style={S.summary} data-testid={`ic-source-toggle-${i}`}>Show the text we read this from</summary>
              <pre style={S.source} data-testid={`ic-source-${i}`}>{r.source_block}</pre>
            </details>
          )}
        </div>
      ))}

      <div style={S.commitBar}>
        <button className="btn btn-primary" onClick={commit} disabled={!canCommit} data-testid="ic-commit">
          {busy ? 'Adding…' : `Add ${included.length} ${included.length === 1 ? 'entry' : 'entries'} to my CV`}
        </button>
        {!specialtyId && <span style={S.hint} data-testid="ic-need-specialty">Choose a specialty first.</span>}
        {specialtyId && needSection > 0 && <span style={S.hint} data-testid="ic-need-section">{needSection} included {needSection === 1 ? 'entry needs' : 'entries need'} a section.</span>}
      </div>
      {error && <p style={S.err} data-testid="ic-error">{error}</p>}
    </main>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 760, margin: '40px auto', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  back: { marginLeft: 0, paddingLeft: 0, fontSize: 'var(--fs-small)', marginBottom: 'var(--space-2)' },
  help: { color: 'var(--muted)', margin: 'var(--space-1) 0 var(--space-4)' },
  locked: { margin: 'var(--space-1) 0 var(--space-4)', fontSize: 'var(--fs-body)', color: 'var(--ink-subtle)' },
  label: { display: 'block', margin: 'var(--space-2) 0', fontSize: 'var(--fs-body)' },
  input: { display: 'block', width: '100%', padding: 'var(--space-2)', marginTop: 2 },
  sel: { padding: 'var(--space-2)', fontSize: 'var(--fs-body)' },
  // Field NAME = a subtle muted chip (scaffolding); the user's VALUES stay in the normal --ink input register.
  fieldName: { display: 'inline-block', background: 'var(--surface-subtle)', color: 'var(--ink-subtle)', fontSize: 'var(--fs-micro)', fontWeight: 600, letterSpacing: '0.02em', padding: '2px 7px', borderRadius: 4, marginBottom: 'var(--space-1)' },
  fieldHint: { fontSize: 'var(--fs-label)', color: 'var(--muted)', fontWeight: 400 },
  sectionWrap: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
  card: { border: '1px solid var(--rule)', borderRadius: 10, padding: 'var(--space-4)', margin: 'var(--space-3) 0', background: 'var(--card)' },
  cardOff: { opacity: 0.5 },
  cardHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' },
  includeLbl: { fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' },
  dates: { display: 'flex', gap: 'var(--space-2)' },
  dateLbl: { flex: 1, fontSize: 'var(--fs-body)' },
  ta: { width: '100%', padding: 'var(--space-2)', marginTop: 2, fontFamily: 'inherit' },
  warn: { fontSize: 'var(--fs-small)', color: 'var(--warning)', margin: 'var(--space-2) 0 0' },
  details: { marginTop: 'var(--space-2)' },
  summary: { fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)', cursor: 'pointer' },
  source: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto', background: 'var(--surface-subtle)', border: '1px solid var(--rule)', borderRadius: 6, padding: 'var(--space-3)', fontSize: 'var(--fs-label)', fontFamily: 'inherit', marginTop: 'var(--space-2)' },
  commitBar: { display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', margin: 'var(--space-5) 0 0', position: 'sticky', bottom: 0, background: 'var(--canvas)', padding: 'var(--space-3) 0' },
  hint: { fontSize: 'var(--fs-small)', color: 'var(--muted)' },
  err: { color: 'var(--danger)', marginTop: 'var(--space-3)' },
};
